import React, { useState } from 'react';
import { camelizeKeys } from 'humps';
import { isValid } from 'date-fns';

import { safeFormat } from '../../utils/dateUtils';
import cx from 'classnames';

import { getNotCompletedIncomePriceColor, getRouteColor, OrderType } from '../../utils/utils';
import { Status } from '../../components/Status';
import { ServiceBadges } from '../../components/TimeLine/ServiceBadges';
import { SmsMessageModal } from './SmsMessageModal';
import { OrderDateTimeConfirmationModal } from '../RouteOrder/OrderDateTimeConfirmationModal';
import { getErrorRouteColor } from '../../helpers/getErrorRouteColor';
import { canViewTransportPrices } from '../../helpers/permissions';

export const TimeLineItem = (props) => {
    const [loading, setLoading] = useState(true)
    const [disabled, setDisabled] = useState(false)

    const {
        order,
        stage,
        unloadingTime,
        index,
        arrivalDatetime,
        distance,
        speed,
        orderId,
        dailyBreakMinutes,
        editable,
        onUnloadingTimeChange,
        onDailyBreakMinutesChange,
        calculatedArrivalTime,
        statuses,
        isValid,
        confirmedArrivalTime,
        calculatedConfirmedArrivalTime,
        validatedRouteOrder,
        orderMissingServicesPrices,
        orderNumber,
        routeId,
        isAdmin,
        hasReturnPicking = false,
        onRemoveOrder,
        onStatusChange = () => { },
        onAddProducts = () => { },
        onProductChange = () => { },
    } = props;

    const {
        id,
        trackingId,
        senderOrderId,
        cash,
        currency,
        sender,
        returnRecipientName,
        note,
        orderComments,
        parcelDescription,
        incomePrice,
        deliveryPostcode,
        deliveryCity,
        deliveryStreet,
        deliveryHouse,
        deliveryPhone,
        deliverySecPhone,
        deliveryFirstName,
        deliveryLastName,
        deliveryLocale,
        internalNote,
        deliveryConfirmations,
        smsMessages,
        isReturnPicking: orderIsReturnPicking,
    } = order || {};
    const isReturnPicking = hasReturnPicking && Boolean(orderIsReturnPicking);

    // Defense-in-depth: a routeOrder without an `order` payload should never
    // normally reach this component, but stale-directions race conditions in
    // renderTimeLine (see RouteEdit.js coherence guard) historically produced
    // partially-hydrated entries, and unguarded `order.*` access here caused
    // the white screen reported on 2026-04-21. Skip rendering such entries
    // rather than crashing the whole TimeLine.
    if (!order) {
        return null;
    }

    let isValidatedRouteOrder = true;

    // Safe against orders that lack delivery confirmations (fresh or
    // partially-hydrated route orders returned from renderTimeLine).
    const routeDeliveryConfirmations = (deliveryConfirmations ?? []).filter((confirmation) => confirmation.routeId === routeId);

    if(editable) {
        isValidatedRouteOrder = validatedRouteOrder ? (validatedRouteOrder.isValid ?? true) : true;
    }

    const confirmDeliveryConfirmation = async (event) => {
        event.preventDefault();
        setDisabled(true);
        setLoading(true);

        try {
            const response = await fetch(`/admin/order-delivery-confirmations/${id}/confirm`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                },
            });

            const data = await response.json();

            setLoading(false);
            setDisabled(false);
            onStatusChange(camelizeKeys(data));
        } catch (error) {
            setLoading(false);
            setDisabled(false);
        }
    }

    const confirmDeliveryConfirmationWithoutNotification = async (event) => {
        event.preventDefault();
        setDisabled(true);
        setLoading(true);

        try {
            const response = await fetch(`/admin/order-delivery-confirmations/${id}/confirm-without-notification`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                },
            });

            const data = await response.json();

            setLoading(false);
            setDisabled(false);
            onStatusChange(camelizeKeys(data));
        } catch (error) {
            setLoading(false);
            setDisabled(false);
        }
    }

    const cancelAsAdminDeliveryConfirmation = async (event) => {
        event.preventDefault();
        setDisabled(true);
        setLoading(true);

        try {
            const response = await fetch(`/admin/order-delivery-confirmations/${id}/cancel-by-admin`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                },
            });

            const data = await response.json();

            setLoading(false);
            setDisabled(false);
            onStatusChange(camelizeKeys(data));
        } catch (error) {
            setLoading(false);
            setDisabled(false);
        }
    }

    const cancelAsSenderDeliveryConfirmation = async (event) => {
        event.preventDefault();
        setDisabled(true);
        setLoading(true);

        try {
            const response = await fetch(`/admin/order-delivery-confirmations/${id}/cancel-by-sender`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                },
            });

            const data = await response.json();

            setLoading(false);
            setDisabled(false);
            onStatusChange(camelizeKeys(data));
        } catch (error) {
            setLoading(false);
            setDisabled(false);
        }
    }

    const cancelAsCustomerDeliveryConfirmation = async (event) => {
        event.preventDefault();
        setDisabled(true);
        setLoading(true);

        try {
            const response = await fetch(`/admin/order-delivery-confirmations/${id}/cancel-by-customer`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                },
            });

            const data = await response.json();

            setLoading(false);
            setDisabled(false);
            onStatusChange(camelizeKeys(data));
        } catch (error) {
            setLoading(false);
            setDisabled(false);
        }
    }

  const refreshOrder = async () => {
    setDisabled(true);
    setLoading(true);

    try {
      const response = await fetch(`/admin/routes/refreshMapOrder/${order.id}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
        },
      });

      if (!response.ok) {
        toastr.error(__('routes.something_went_wrong'));
        return;
      }

      const data = await response.json();

      setLoading(false);
      setDisabled(false);
      onStatusChange(camelizeKeys(data));
    } finally {
      setLoading(false);
      setDisabled(false);
    }
  }

    function isNotCompletedIncomePrice() {
      // Partially-hydrated route orders (attached via marker click between
      // Optimize runs and then processed by a stale renderTimeLine pass) may
      // arrive without incomePrice; returning false here prevents the
      // downstream getRouteOrderColor() → JSX chain from throwing and
      // producing a white screen.
      return incomePrice?.notPricedPackages != null && incomePrice.notPricedPackages.length > 0;
    }

    function isMissingServicePrice() {
      const orderMissing = orderMissingServicesPrices?.find(o => o?.orderId === orderId);

      return orderMissing?.packageIds?.length > 0;
    }

    function isWarning() {
      return isNotCompletedIncomePrice() || isMissingServicePrice();
    }

    function getRouteOrderColor() {
      return isWarning() ? getNotCompletedIncomePriceColor() : 'transparent';
    }

    const getSafeConfirmedArrivalDate = (value) => {
        try {
            return safeFormat(value, 'dd.MM.yyyy');
        } catch (error) {
            return '—';
        }
    };

    function formatDate(date) {
        date = new Date(date);

        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    }

  const [isSmsModalOpen, setIsSMSModalOpen] = useState(false);

  const handleSmsModalClose = () => setIsSMSModalOpen(false);
  const handleSmsModalOpen = () => setIsSMSModalOpen(true);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const handleConfirmationModalClose = () => setIsConfirmationModalOpen(false);
  const handleConfirmationModalOpen = () => setIsConfirmationModalOpen(true);

    return (
        <>
            <li className="timeline-item timeline-icon-primary active">
                <div className="row m-0">
                    <div className={`timeline-item-label ${isValidatedRouteOrder ? 'col-4' : 'col-7'}`} style={{ backgroundColor: isValidatedRouteOrder ? getRouteColor(stage) : getErrorRouteColor() }}>
                      <p className="tx-07 lp mb-0 lh-1-3rem opa-05">{('0' + orderNumber).slice(-2)}</p>
                      <p className="tx-11 text-bold-700 mb-0 lh-1-3rem">
                        {(!isValidatedRouteOrder && validatedRouteOrder &&
                            <>
                              {validatedRouteOrder.oldArrivalDate}
                            </>)
                          ||
                          <>
                            {safeFormat(arrivalDatetime, 'dd.MM.yyyy')}
                          </>
                        }

                        <span className="tx-11 text-bold-700 mb-0 lh-1-3rem">
                            <span>&nbsp;&ndash;&nbsp;</span>
                          {safeFormat(arrivalDatetime, 'HH:mm')}
                          </span>

                        {!isValidatedRouteOrder && validatedRouteOrder &&
                          <>
                            <span className="mx-1">&rarr;</span>
                            {validatedRouteOrder.newArrivalDate}
                          </>
                        }
                      </p>
                      {calculatedArrivalTime &&
                        <>
                          <p className="tx-11 lp mb-0 lh-1-3rem opa-05">
                            {(!isValidatedRouteOrder && validatedRouteOrder) ? (
                              <>
                                {validatedRouteOrder.oldArrivalHours}
                                <span className="mx-1">&rarr;</span>
                                {validatedRouteOrder.newArrivalHours}
                              </>
                            ) : (
                              calculatedArrivalTime
                            )}
                          </p>
                        </>
                      }
                    </div>

                    {isValid === false && (
                      <>
                        <div className="ml-1 timeline-item-label col-4 bg-warning">
                          <p className="tx-11 lp mb-0 lh-1-3rem">
                            {__('routes.confirmed_arrival')}
                          </p>

                          <p className="tx-11 text-bold-700 mb-0 lh-1-3rem">
                            {getSafeConfirmedArrivalDate(confirmedArrivalTime)}
                          </p>

                          <p className="tx-11 lp mb-0 lh-1-3rem opa-05">
                            {calculatedConfirmedArrivalTime}
                          </p>
                        </div>
                      </>
                    )}
                </div>

                <div className="timeline-info timeline-info--with-border" style={{ backgroundColor: getRouteOrderColor(), borderColor: isValidatedRouteOrder ? getRouteColor(stage) : getErrorRouteColor() }}>
                    <div className="row col-12 p-0 m-0">
                        <div className="col-4 pt-1 pb-1 border-right">
                            <h1 className="text-primary tx-15 mb-0 text-bold-700 d-flex justify-content-between align-items-center">
                                <a href={`/${window.urlPrefix}/orders/show/${id}`}>{trackingId}</a>

                                {order?.type === 'return' && (
                                  <div className="badge badge-black text-bold-600">
                                    { __('routes.map_info.return') }
                                  </div>
                                )}

                                {hasReturnPicking && onRemoveOrder && (
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm ml-1"
                                    onClick={() => {
                                      if (window.confirm(__('order_returns.route.remove_order_confirm'))) {
                                        onRemoveOrder(orderId);
                                      }
                                    }}
                                  >
                                    {__('order_returns.route.remove_order')}
                                  </button>
                                )}
                            </h1>
                            <p className="mb-0 lh-1">{senderOrderId}</p>
                            <p className="text-bold-700 tx-08 mb-1 mt-1 lh-1 pt-2px">{parcelDescription}</p>
                            {!editable && (
                                <>
                                    <p className="tx-08 mb-0 lh-1">Nadawca: {sender?.name ?? '—'}</p>
                                </>
                            )}
                            <p className="tx-08 mb-0 lh-1">Il. paczek: {order?.packages?.length ?? 0}</p>
                            {canViewTransportPrices() && !hasReturnPicking && (
                              <div data-testid="timeline-income">
                                <p className="tx-08 mb-0 lh-1">
                                  {__('info_window.income.total', {value: order?.incomePrice?.price?.amount ?? 0, currency: order?.incomePrice?.price?.currency ?? ''})}

                                  {isNotCompletedIncomePrice() && (
                                    <>
                                      <span className="text-danger font-weight-bold">&nbsp;!!!</span>
                                    </>
                                  )}
                                </p>

                                <p className="tx-08 mb-0 lh-1">
                                  {__('info_window.income.order_income', {value: order?.incomePrice?.delivery?.amount ?? 0, currency: order?.incomePrice?.delivery?.currency ?? ''})}
                                </p>

                                <p className="tx-08 mb-0 lh-1">
                                  {__('info_window.income.exchange_income', {value: order?.incomePrice?.return?.amount ?? 0, currency: order?.incomePrice?.return?.currency ?? ''})}
                                </p>
                              </div>
                            )}

                            <p className="tx-08 mb-0 lh-1">{__('routes.map_info.products')}</p>

                            {order?.productNames?.filter(Boolean).length > 0 ? (
                              <p className="tx-08 mb-0 lh-1">{order.productNames.map((name) => <span className='ml-3px'>- {name}</span>)}</p>
                            ) : (
                              <p className="tx-08 mb-0 lh-1 ml-3px">{__('routes.map_info.no_products')}</p>
                            )}

                        </div>

                        <div className='col-auto pt-1 pb-1 border-right'>
                          <h5 className="text-bold-600 mb-0 mt-3px">
                            { __('packages.package_weight', { weight: order?.packagesWeight ?? 0 }) }

                            <br />

                            { __('packages.package_volume_m3', { volume: order?.packagesVolumeM3 ?? 0 }) }
                          </h5>

                          <span className="font-small-2 text-muted">
                                  { __('orders.packages_dimensions') }
                              </span>
                        </div>

                        <div
                            className="col-3 pt-1 pb-1 border-right"
                            data-testid={isReturnPicking ? 'return-recipient' : undefined}
                        >
                            {isReturnPicking ? (
                                <p className="text-bold-700 tx-1 mb-0">
                                    {returnRecipientName || '—'}
                                </p>
                            ) : (
                                <>
                                    <p className="text-bold-700 tx-1 mb-0">
                                        {deliveryPostcode}, <span className="text-bold-400 tx-08 mb-0">{deliveryCity}</span>
                                    </p>
                                    <p className="tx-08 mb-0 lh-1">
                                        {deliveryStreet} {deliveryHouse}
                                    </p>
                                    <p className="tx-1 mt-1 mb-5px">
                                        {deliveryFirstName} {deliveryLastName}
                                    </p>
                                    <p className="tx-09 mb-5px">
                                        <span className="tx-08 mb-0 lh-1">
                                            Tel.:
                                        </span>
                                        <span className="text-bold-700 tx-08 mb-0 lh-1">
                                            <a href={`tel:${deliveryPhone}`}>{deliveryPhone}</a>
                                        </span>
                                    </p>
                                    <p className='tx-09 mb-5px'>
                                        <span className='tx-08 mb-0 lh-1'>
                                            Dodatk. tel.:
                                        </span>
                                        {deliverySecPhone ? (
                                            <span className='text-bold-700 tx-08 mb-0 lh-1'>
                                                <a href={`tel:${deliverySecPhone}`}>{deliverySecPhone}</a>
                                            </span>
                                        ) : (
                                            <>—</>
                                        )}
                                    </p>
                                    {!editable && (
                                        <p className='tx-08 mb-0 mt-07 lh-1 pt-2px'>
                                            Pobranie:
                                            <span className='text-bold-700'> {cash} {currency}</span>
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                        <div className='col-2 pt-1 pb-1'>
                            <Status order={order} editable={!editable} statuses={statuses} onStatusChange={onStatusChange} />

                            {order?.isUrgent && (
                              <>
                                      <span className="badge badge-pill badge-light-danger font-small-2 mt-8px">
                                          { __('orders.is_urgent') }
                                      </span>
                              </>
                            )}

                            {order?.isExchange && (
                              <>
                                  <span className="badge badge-pill badge-light-secondary font-small-2 mt-8px">
                                      { __('orders.is_exchange') }
                                  </span>
                              </>
                            )}

                            {order?.orderServices?.length > 0 && <>
                                <p className='text-muted tx-07 mt-8px mb-0'>Usługi</p>
                                <ServiceBadges services={order.orderServices} />
                            </>}
                            <p className='text-muted tx-07 mt-1 mb-0'>{__('routes.unloading_time')}</p>
                            <div className='tx-08'>
                                {editable ? (
                                    <>
                                        <input
                                            type="number"
                                            className="break_time"
                                            value={unloadingTime}
                                            onChange={(event) => onUnloadingTimeChange(id, event)}
                                        />{' '}
                                        min
                                    </>
                                ) : (
                                    <>
                                        {unloadingTime} min
                                    </>
                                )}
                            </div>

                            { order?.undeliveredInfo?.amount > 0 && (
                              <div className='mt-1'>
                                <p className='tx-08 mb-0 lh-1 danger'>{__('orders.undelivered', { amount: order.undeliveredInfo.amount })}</p>
                                <p className='tx-08 mb-0 lh-1 danger'>{__('orders.last_try', { date: order.undeliveredInfo.lastDate ?? '-' })}</p>
                              </div>
                            )}
                        </div>
                        <div className="col-2 pt-1 pb-1 border-left">
                            <p className="locale-label tx-08 mb-1">{deliveryLocale}</p>
                            {!editable && (
                                <>
                                    <p className="text-muted tx-07 mb-0 lh-1">Data</p>
                                    <p className="tx-08 mb-0 lh-1">
                                        {safeFormat(arrivalDatetime, 'dd-MM-yyyy')}
                                        <br />
                                        <br />
                                    </p>
                                </>
                            )}
                            <p className="text-muted tx-07 mb-0 lh-1">Odcinek</p>
                            <p className="tx-08 mb-0 lh-1">{distance} km</p>
                            {speed !== undefined && <p className="tx-08 mb-0 lh-1">{speed} km/h</p>}
                            {order?.media?.length > 0 && (
                                <p className="tx-08 mt-1 lh-1 text-success">
                                    <i className='bx bx-edit font-size-base'></i> Odbiorca złożył podpis
                                </p>
                            )}
                        </div>
                    </div>


                    <div className="border-top d-flex flex-row flex-nowrap">
                      <div className='col-8'>
                        {smsMessages?.length > 0 ? (
                            <div className="p-1">
                                {smsMessages.map((smsMessage) => (
                                    <div key={smsMessage.id} className={cx("rounded d-flex flex-column mb-1", {
                                      'align-items-end timeline-sms-admin': smsMessage.sender.includes('system'),
                                      'align-items-start timeline-sms-customer': !smsMessage.sender.includes('system'),
                                    })}>
                                        <p className='text-primary font-size-base mb-0 mx-2'>
                                            {smsMessage.message}
                                        </p>
                                        <p className='text-muted font-size-base mb-0 mx-2'>
                                            {formatDate(smsMessage.date)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='p-1'>
                                Aktualnie nie istnieją żadne wiadomości SMS przypisane do zamówienia.
                            </div>
                        )}
                      </div>

                      {isAdmin && !isReturnPicking && (
                        <div className='border-left p-1 col-4 d-flex flex-wrap align-content-center'>
                          <button
                            type="button"
                            className="btn btn-secondary mr-1"
                            onClick={handleSmsModalOpen}
                          >
                            {__('orders.modal.send_sms.send_sms')}
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary mr-3"
                            onClick={refreshOrder}
                            disabled={disabled}
                          >
                            {__('routes.refresh_order')}
                          </button>
                        </div>
                      )}

                    </div>

                    <SmsMessageModal isOpen={isSmsModalOpen} onModalClose={handleSmsModalClose} order={order} />

                    <OrderDateTimeConfirmationModal
                      isOpen={isConfirmationModalOpen}
                      onModalClose={handleConfirmationModalClose} order={order}
                      onStatusChange={onStatusChange}
                    />

                    {routeDeliveryConfirmations?.length > 0 ? (
                        <div className="border-top d-flex flex-row flex-nowrap">
                            <div className='p-1 text-success col-8'>
                                {routeDeliveryConfirmations.map(({ id, description, color, isPendingExpired, createdAt, confirmedArrivalHours, confirmedArrivalDate, status }) => (
                                    <div key={id} className='font-weight-bold mb-5px' style={{ color: color }}>
                                        {isPendingExpired ? __('routes.delivery_confirmation_description', {'description': description}) : description}
                                        {status === 'confirmed' && (
                                            <span className='text-muted font-weight-normal'> - {confirmedArrivalDate} {confirmedArrivalHours}</span>
                                        )}
                                        <div className='text-muted font-weight-normal tx-06'>({formatDate(createdAt)})</div>
                                    </div>
                                ))}
                            </div>
                            <div className='border-left p-1 col-4 d-flex flex-wrap align-content-start'>
                              {isAdmin && !isReturnPicking && (
                                <>
                                  {routeDeliveryConfirmations[0].status !== 'confirmed' && (
                                    <>
                                      {(routeDeliveryConfirmations[0].status !== 'pending' || routeDeliveryConfirmations[0].isPendingExpired) && (
                                        <button
                                          type="button"
                                          className="btn btn-secondary waves-effect waves-light mt-5px mr-10px"
                                          onClick={handleConfirmationModalOpen}
                                        >
                                          {__('routes.ask_for_confirmation')}
                                        </button>
                                      )}
                                      <form className='mb-0' onSubmit={confirmDeliveryConfirmation}>
                                        <button className='btn btn-success waves-effect waves-light mt-5px mr-10px' disabled={disabled}>Potwierdź dostawę</button>
                                      </form>
                                    </>
                                  )}

                                  <>
                                    {!['canceled_by_admin', 'canceled_by_sender', 'canceled_by_customer'].includes(routeDeliveryConfirmations[0].status) && (
                                      <>
                                        <form className='mb-0' onSubmit={cancelAsAdminDeliveryConfirmation}>
                                          <button className='btn btn-danger waves-effect waves-light mt-5px mr-10px' disabled={disabled}>Anuluj jako przewoźnik</button>
                                        </form>
                                        <form className='mb-0' onSubmit={cancelAsSenderDeliveryConfirmation}>
                                          <button className='btn btn-danger waves-effect waves-light mt-5px mr-10px' disabled={disabled}>Anuluj jako nadawca</button>
                                        </form>
                                        <form className='mb-0' onSubmit={cancelAsCustomerDeliveryConfirmation}>
                                          <button className='btn btn-danger waves-effect waves-light mt-5px mr-10px' disabled={disabled}>Anuluj jako klient</button>
                                        </form>
                                      </>
                                    )}
                                  </>
                                </>
                              )}
                            </div>
                        </div>
                    ) : (
                        <div className='border-top d-flex'>
                            <div className='p-1 col-8'>
                                <p>
                                  Nie wysłano zapytania o dostawę.
                                </p>

                                {isNotCompletedIncomePrice() && (
                                  <>
                                    <p className={'text-danger mb-0'}>
                                      {__('routes.warnings.missing_income_price')}
                                    </p>
                                  </>
                                )}

                                {isMissingServicePrice() && (
                                  <>
                                    <p className={'text-danger mb-0'}>
                                      {__('routes.warnings.missing_package_services')}
                                    </p>
                                  </>
                                )}
                            </div>

                            <div className='border-left p-1 col-4'>
                                {!editable && isAdmin && !isReturnPicking && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-secondary waves-effect waves-light mt-5px mr-10px"
                                        onClick={handleConfirmationModalOpen}
                                      >
                                        {__('routes.ask_for_confirmation')}
                                      </button>

                                      <form className='mb-0' onSubmit={confirmDeliveryConfirmationWithoutNotification}>
                                          <button className='btn btn-success waves-effect waves-light mt-5px mr-10px' disabled={disabled}>Potwierdź zamówienie bez wysłania prośby o potwierdzenie</button>
                                      </form>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {deliveryConfirmations?.length > 0 && (
                      <div className="border-top d-flex flex-row flex-nowrap">
                        <div className='p-1 text-success col-8'>
                          {deliveryConfirmations
                            .filter((confirmation) => confirmation.routeId !== routeId)
                            .map(({ id, description, color, isPendingExpired, createdAt, confirmedArrivalHours, confirmedArrivalDate, status }) => (
                              <div key={id} className='font-weight-bold mb-5px' style={{ color: color, opacity: 0.5 }}>
                                {isPendingExpired ? __('routes.delivery_confirmation_description', {'description': description}) : description}
                                {status === 'confirmed' && (
                                  <span className='text-muted font-weight-normal'> - {confirmedArrivalDate} {confirmedArrivalHours}</span>
                                )}
                                <div className='text-muted font-weight-normal tx-06'>({formatDate(createdAt)})</div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className='border-top p-1'>
                      {orderComments?.length > 0 ? (
                        <>
                          {orderComments.map((orderComment, index) => (
                            <div id={'orderComment' + index}>
                              <span className='text-bold-600'>{orderComment.user?.firstName} {orderComment.user?.lastName}</span>

                              <span> - {orderComment.message}</span>
                            </div>
                          ))}
                        </>
                      ) : (
                          <span>{__('order_comments.none')}</span>
                      )}
                    </div>

                    {note && (
                        <div className='border-top p-1 text-danger'>
                            <i className='bx bx-error-circle' /> {note}
                        </div>
                    )}
                    {internalNote && (
                        <div className='border-top p-1 text-warning'>
                            <i className='bx bx-error-circle' /> {internalNote}
                        </div>
                    )}
                </div>
            </li>
        </>
    );
};
